import { cva, type VariantProps } from 'class-variance-authority';

/**
 * Toggle (switch) visual variants — mirror the Figma "Flashka Design System"
 * file `YZK9r3wDhvw1pY60KlVEIj`, frame `1665:14429` ("Toggle"). Reference
 * nodes (the XS band, one per state — the larger sizes scale the same way):
 *   - 1749:2448 – XS / Default / Active=No   (inactive anatomy)
 *   - 1749:2454 – XS / Default / Active=Yes  (active anatomy)
 *   - 1749:2466 – XS / Disabled / Active=No  (= inactive + opacity-50)
 *   - 1749:2469 – XS / Disabled / Active=Yes (= active + opacity-50)
 *   - 1749:2498 – XS / Focused / Active=No   (1px secondary-600 ring + p-px gap)
 *   - 1749:2497 – XS / Focused / Active=Yes  (1px primary-900 ring + p-px gap)
 *   - 1749:2006/2072/2132 – Small/Medium/Large active (size scale + radius)
 *
 * Anatomy (3 layers, faithful to the Figma node structure):
 *   1. A borderless, transparent `Pressable` ROOT that owns the size + radius
 *      and (web-only) the keyboard focus ring. It's the positioning context.
 *   2. A `Background` view (`absolute inset-0`) painting the track: 1px border,
 *      fill, and a `web:`-gated inset shadow.
 *   3. A `knob` view (`absolute top-0`, full-height square) that slides from
 *      `left-0` (inactive) to `left-{trackHeight}` (active).
 *   Because the ROOT carries NO border, `inset-0` / `top-0` / `left-*` resolve
 *   against the full track box — the knob and track align edge-to-edge exactly
 *   as in Figma (the knob's border sits on top of the track's on the filled
 *   half; the track border shows on the empty half).
 *
 * Axes:
 *   size:   xs (24px) · s (28px) · m (32px, default) · l (36px). The track is
 *           2× the height wide; the knob is a full-height square; the active
 *           knob offset == the track height.
 *   active: the on/off (checked) selection axis.
 *
 * Colors (light, from `get_variable_defs` on the frame):
 *   inactive track  bg white/70 (Figma `secondary/30` #ffffffb2) · border secondary-200
 *   inactive knob   bg secondary-50 (#fff) · border secondary-300
 *   active   track  bg primary-100 · border primary-400
 *   active   knob   bg primary-300 · border primary-500
 *   focus ring      inactive secondary-600 · active primary-900
 *   inset shadow    inset 0 2 6 rgba(0,0,0,0.1)  (web class; native boxShadow)
 *
 * Radius scales as `height / 2 − 2` → 10 · 12 · 14 · 16 px (a soft squircle,
 * not a full pill). The same value is shared by the root, the track, and the
 * square knob.
 *
 * Disabled: `opacity-50` (Figma nodes 1749:2466/2469) — supplied automatically
 * by the `Pressable` primitive (web `disabled:opacity-50`; native
 * `cn(disabled && 'opacity-50')`), so no CVA override is needed. Matches the
 * Chip's opacity-50 spec (differs from Button's opacity-30).
 *
 * Cross-platform: the `web:`-gated transition / focus-ring / `shadow-[inset…]`
 * classes stay off native (NativeWind v4 can't render them reliably on RN
 * 0.83). `Toggle.native.tsx` re-creates the equivalents with RN-core APIs:
 * the knob-slide + colour cross-fade run on `Animated` (these same CVA classes),
 * and the track's inset shadow is applied via the RN `boxShadow` style (new
 * arch renders `inset` on iOS). Both platforms animate the flip in 150ms with
 * the Figma "ease-out-back" curve `cubic-bezier(0.34, 1.56, 0.64, 1)`.
 *
 * Known divergence (designer follow-up): the Large default-active track border
 * is `primary-600` in Figma (node 1749:2132) while XS/Small/Medium — and
 * the Large *focused*-active inner border — all use `primary-400`. Treated
 * as an accidental drift and normalized to `primary-400` for all sizes.
 *
 * Dark mode is EXACT, not extrapolated. Verified against the dark Figma frame
 * `3178:28504` via `get_variable_defs`: every token there equals the light
 * class name's auto-inverted value (the CSS-var palette mirrors each shade
 * `dark = 1000 − light`, `500` fixed — see `generate-css-vars.cjs`). So e.g.
 * `bg-primary-100` → dark `#4d179a` (Figma's active-track dark), `bg-secondary-50`
 * knob → dark `#0a0a0a`, ring `secondary-600` → dark `#a1a1a1`, all matching the
 * frame with no `dark:` swaps. The lone exception is the track's translucent
 * `secondary/30` fill (`white`/`black` literals don't invert) — see below.
 */

/** Per-size geometry (track height · radius). Shared by root + track + knob. */
const SIZE_TRACK = {
  xs: 'h-6 w-12 rounded-[10px]',
  s: 'h-7 w-14 rounded-[12px]',
  m: 'h-8 w-16 rounded-[14px]',
  l: 'h-9 w-[72px] rounded-[16px]',
} as const;

/**
 * The transparent, borderless `Pressable` that holds the track + knob and owns
 * the size, the radius, and the (web-only) keyboard focus ring. The ring color
 * depends on the active state (Figma focus nodes 1749:2498 / 2497) and uses the
 * proven `ring-1 + ring-offset-1` recipe (a 1px ring set off by a 1px gap) that
 * mirrors the design's "outer border + p-px" focus wrapper — same pattern as
 * Chip/SegmentedControl, and it overrides the Pressable primitive's default gray ring.
 */
export const toggleRootVariants = cva(
  'relative inline-flex shrink-0 ' +
    'web:focus-visible:ring-1 web:focus-visible:ring-offset-1',
  {
    variants: {
      size: SIZE_TRACK,
      active: {
        false: 'web:focus-visible:ring-secondary-600',
        true: 'web:focus-visible:ring-primary-900',
      },
    },
    defaultVariants: { size: 'm', active: false },
  },
);

/**
 * The track background (`absolute inset-0`): 1px border + fill + a web-only
 * inset shadow. Sits behind the knob.
 */
export const toggleTrackVariants = cva(
  'absolute inset-0 border ' +
    'web:shadow-[inset_0px_2px_6px_0px_rgba(0,0,0,0.1)] ' +
    'web:transition-[background-color,border-color] web:duration-150 web:ease-[cubic-bezier(0.34,1.56,0.64,1)]',
  {
    variants: {
      size: {
        xs: 'rounded-[10px]',
        s: 'rounded-[12px]',
        m: 'rounded-[14px]',
        l: 'rounded-[16px]',
      },
      active: {
        // Track fill is Figma's translucent `secondary/30` token — white@70%
        // (light) / black@70% (dark). `white`/`black` are literals that don't
        // auto-invert, so this is the ONE explicit `dark:` swap the toggle
        // needs; every other tone below inverts via the CSS-var palette.
        false: 'bg-white/70 dark:bg-secondary-100/70 border-secondary-200 ',
        true: 'bg-primary-100 border-primary-400 ',
      },
    },
    defaultVariants: { size: 'm', active: false },
  },
);

/**
 * The knob (`absolute top-0`, full-height square). Slides from `left-0`
 * (inactive) to `left-{trackHeight}` (active) — animated on web via the
 * `left` transition, and on native via an `Animated` `translateX`
 * (`Toggle.native.tsx`) that overrides this `left-*` offset.
 */
export const toggleKnobVariants = cva(
  'absolute top-0 left-0 border ' +
    'web:transition-[left,background-color,border-color] web:duration-150 web:ease-[cubic-bezier(0.34,1.56,0.64,1)]',
  {
    variants: {
      size: {
        xs: 'h-6 w-6 rounded-[10px]',
        s: 'h-7 w-7 rounded-[12px]',
        m: 'h-8 w-8 rounded-[14px]',
        l: 'h-9 w-9 rounded-[16px]',
      },
      active: {
        false: 'bg-secondary-50 border-secondary-300',
        true: 'bg-primary-300 border-primary-500',
      },
    },
    compoundVariants: [
      // Active → knob slides right by its own width (== the track height), so
      // its right edge lands flush with the track's right edge.
      { active: true, size: 'xs', class: 'left-6' },
      { active: true, size: 's', class: 'left-7' },
      { active: true, size: 'm', class: 'left-8' },
      { active: true, size: 'l', class: 'left-9' },
    ],
    defaultVariants: { size: 'm', active: false },
  },
);

export type ToggleVariantProps = VariantProps<typeof toggleRootVariants>;
